import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import {
  Upload,
  Edit,
  Trash2,
  Plus,
  Save,
  X,
  ImageIcon,
  Link,
  Users,
  Heart,
  Target,
  Building,
  MessageCircle,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Eye,
  ExternalLink,
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Check
} from 'lucide-react';
import { postWithTokenAPI, getWithTokenAPI, putWithTokenAPI, deleteWithTokenAPI, BASE_URL } from '../../util/Api';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const ContentManagementSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { userId } = useSelector((state) => state.auth)
  console.log("userId", userId);

  // Parse URL to get initial tab states
  const getInitialTabState = () => {
    const queryString = location.search;

    // Check for header tabs
    if (queryString.includes('Header/')) {
      const headerTab = queryString.split('Header/')[1];
      return {
        activeTab: 'header',
        headerActiveTab: headerTab || 'main-content'
      };
    }
    // Check for footer tabs
    else if (queryString.includes('Footer/')) {
      const footerTab = queryString.split('Footer/')[1];
      return {
        activeTab: 'footer',
        footerActiveTab: footerTab || 'brand-info'
      };
    }
    // Check for simple header/footer
    else if (queryParams.get("Header")) {
      return {
        activeTab: 'header',
        headerActiveTab: 'main-content'
      };
    }
    else if (queryParams.get("Footer")) {
      return {
        activeTab: 'footer',
        footerActiveTab: 'brand-info'
      };
    }
    // Default
    return {
      activeTab: 'header',
      headerActiveTab: 'main-content'
    };
  };

  const initialTabState = getInitialTabState();

  const [activeTab, setActiveTab] = useState(initialTabState.activeTab);
  const [headerActiveTab, setHeaderActiveTab] = useState(initialTabState.headerActiveTab);
  const [footerActiveTab, setFooterActiveTab] = useState(initialTabState.footerActiveTab);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [headerData, setHeaderData] = useState(null);
  const [footerData, setFooterData] = useState(null);
  const [termsData, setTermsData] = useState(null);
  const [privacyData, setPrivacyData] = useState(null);
  
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState({ show: false, url: '', title: '' });

  // Consistent styling classes for all inputs
  const inputActiveClass = "border-blue-200 ring-1 ring-blue-100 bg-white text-gray-900 placeholder-gray-500";
  const inputDisabledClass = "border-gray-200 bg-gray-50 text-gray-600 placeholder-gray-400 cursor-not-allowed";

  // Header Form State
  const [headerForm, setHeaderForm] = useState({
    heading: '',
    subHeading: '',
    logo: '',
    browseProfiles: '',
    stats: {
      activeProfiles: '',
      successStories: '',
      matchRate: ''
    }
  });

  // Footer Form State
  const [footerForm, setFooterForm] = useState({
    brandInfo: {
      brandName: '',
      logo: '',
      tagline: '',
      yearsActive: ''
    },
    heading: '',
    subHeading: '',
    socialMedia: {
      facebook: '',
      twitter: '',
      instagram: '',
      youtube: '',
      threads: ''
    },
    contactInfo: {
      email: '',
      phone: '',
      address: ''
    },
    is_facebook_active: true,
    is_twitter_active: true,
    is_instagram_active: true,
    is_youtube_active: true,
    is_threads_active: true
  });

  const [termsForm, setTermsForm] = useState({
    title: '',
    content: '',
    is_deleted: false
  });

  const [privacyForm, setPrivacyForm] = useState({
    title: '',
    content: '',
    is_deleted: false
  });

  const [cancellationData, setCancellationData] = useState(null);
  const [cancellationForm, setCancellationForm] = useState({
    title: '',
    content: '',
    is_deleted: false
  });

  const [shippingData, setShippingData] = useState(null);
  const [shippingForm, setShippingForm] = useState({
    title: '',
    content: '',
    is_deleted: false
  });

  const token = localStorage.getItem('matrimony_token');

  // Back button handler
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Update URL when tabs change
  const updateUrl = (mainTab, subTab = null) => {
    if (subTab) {
      navigate(`/admin/content-management?${mainTab}/${subTab}`);
    } else {
      navigate(`/admin/content-management?${mainTab}`);
    }
  };

  // Handle main tab change
  const handleMainTabChange = (value) => {
    setActiveTab(value);
    setIsEditing(false); // Reset editing state on tab change
    if (value === "header") {
      updateUrl('Header', headerActiveTab);
    } else if (value === "footer") {
      updateUrl('Footer', footerActiveTab);
    } else {
      updateUrl(value, null);
    }
  };

  // Handle header sub tab change
  const handleHeaderSubTabChange = (value) => {
    setHeaderActiveTab(value);
    updateUrl('Header', value);
  };

  // Handle footer sub tab change
  const handleFooterSubTabChange = (value) => {
    setFooterActiveTab(value);
    updateUrl('Footer', value);
  };

  // Image Preview Handler with scroll control
  const handleImagePreview = (url, title) => {
    setImagePreview({ show: true, url, title });
    document.body.style.overflow = 'hidden';
  };

  const closeImagePreview = () => {
    setImagePreview({ show: false, url: '', title: '' });
    document.body.style.overflow = 'unset';
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Fetch Header Data
  const getAllHeader = async () => {
    try {
      const response = await getWithTokenAPI(token, '/header/get_all');
      console.log('getAllHeader response:', response);
      if (response?.statusCode === 200 && response.data?.length > 0) {
        setHeaderData(response.data[0]);
        setHeaderForm({
          heading: response.data[0].heading || '',
          subHeading: response.data[0].subHeading || '',
          logo: response.data[0].logo || '',
          browseProfiles: response.data[0].browseProfiles || '',
          stats: {
            activeProfiles: response.data[0].stats?.activeProfiles || '',
            successStories: response.data[0].stats?.successStories || '',
            matchRate: response.data[0].stats?.matchRate || ''
          }
        });
      } else {
        setHeaderData(null);
        resetHeaderForm();
      }
    } catch (error) {
      console.error('Error fetching header:', error);
      setHeaderData(null);
    }
  };

  // Fetch Footer Data - UPDATED
  const getAllFooter = async () => {
    try {
      const response = await getWithTokenAPI(token, '/footer/get_all');
      console.log('getAllFooter response:', response);
      if (response?.statusCode === 200 && response.data?.length > 0) {
        const footerData = response.data[0];
        setFooterData(footerData);

        // DEBUG: Log the actual data from backend
        console.log('Footer data from backend:', {
          is_facebook_active: footerData.is_facebook_active,
          is_twitter_active: footerData.is_twitter_active,
          is_instagram_active: footerData.is_instagram_active,
          is_youtube_active: footerData.is_youtube_active,
          is_threads_active: footerData.is_threads_active
        });

        setFooterForm({
          brandInfo: {
            brandName: footerData.brandInfo?.brandName || '',
            logo: footerData.brandInfo?.logo || '',
            tagline: footerData.brandInfo?.tagline || '',
            yearsActive: footerData.brandInfo?.yearsActive || ''
          },
          heading: footerData.heading || '',
          subHeading: footerData.subHeading || '',
          socialMedia: {
            facebook: footerData.socialMedia?.facebook || '',
            twitter: footerData.socialMedia?.twitter || '',
            instagram: footerData.socialMedia?.instagram || '',
            youtube: footerData.socialMedia?.youtube || '',
            threads: footerData.socialMedia?.threads || ''
          },
          contactInfo: {
            email: footerData.EmailAddress || '',
            phone: footerData.PhoneNumber || '',
            address: footerData.OfficeAddress || ''
          },
          is_facebook_active: footerData.is_facebook_active !== undefined ? footerData.is_facebook_active : true,
          is_twitter_active: footerData.is_twitter_active !== undefined ? footerData.is_twitter_active : true,
          is_instagram_active: footerData.is_instagram_active !== undefined ? footerData.is_instagram_active : true,
          is_youtube_active: footerData.is_youtube_active !== undefined ? footerData.is_youtube_active : true,
          is_threads_active: footerData.is_threads_active !== undefined ? footerData.is_threads_active : true
        });
      } else {
        setFooterData(null);
        resetFooterForm();
      }
    } catch (error) {
      console.error('Error fetching footer:', error);
      setFooterData(null);
    }
  };

  const getTerms = async () => {
    try {
      const response = await getWithTokenAPI(token, '/terms-condition/get_all');
      if (response?.statusCode === 200 && response.data) {
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        if(data) {
          setTermsData(data);
          setTermsForm({
            title: data.title || '',
            content: data.content || '',
            is_deleted: data.is_deleted || false
          });
        }
      }
    } catch (error) {
      console.error('Error fetching terms:', error);
    }
  };

  const getPrivacy = async () => {
    try {
      const response = await getWithTokenAPI(token, '/privacy-policy/get_all');
      if (response?.statusCode === 200 && response.data) {
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        if(data) {
          setPrivacyData(data);
          setPrivacyForm({
            title: data.title || '',
            content: data.content || '',
            is_deleted: data.is_deleted || false
          });
        }
      }
    } catch (error) {
      console.error('Error fetching privacy:', error);
    }
  };

  const getCancellation = async () => {
    try {
      const response = await getWithTokenAPI(token, '/cancellation-refund-policy/get_all');
      if (response?.statusCode === 200 && response.data) {
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        if(data) {
          setCancellationData(data);
          setCancellationForm({
            title: data.title || '',
            content: data.content || '',
            is_deleted: data.is_deleted || false
          });
        }
      }
    } catch (error) {
      console.error('Error fetching cancellation:', error);
    }
  };

  const getShipping = async () => {
    try {
      const response = await getWithTokenAPI(token, '/shipping-exchange-policy/get_all');
      if (response?.statusCode === 200 && response.data) {
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        if(data) {
          setShippingData(data);
          setShippingForm({
            title: data.title || '',
            content: data.content || '',
            is_deleted: data.is_deleted || false
          });
        }
      }
    } catch (error) {
      console.error('Error fetching shipping:', error);
    }
  };

  // Reset Forms
  const resetHeaderForm = () => {
    setHeaderForm({
      heading: '',
      subHeading: '',
      logo: '',
      browseProfiles: '',
      stats: {
        activeProfiles: '',
        successStories: '',
        matchRate: ''
      }
    });
  };

  const resetFooterForm = () => {
    setFooterForm({
      brandInfo: {
        brandName: '',
        logo: '',
        tagline: '',
        yearsActive: ''
      },
      heading: '',
      subHeading: '',
      socialMedia: {
        facebook: '',
        twitter: '',
        instagram: '',
        youtube: '',
        threads: ''
      },
      contactInfo: {
        email: '',
        phone: '',
        address: ''
      },
      // Reset social media activation fields
      is_facebook_active: true,
      is_twitter_active: true,
      is_instagram_active: true,
      is_youtube_active: true,
      is_threads_active: true
    });
  };

  // Improved Image Upload Handler with SweetAlert
  //   const handleImageUpload = async (file, field, section) => {
  //     if (!file) return;

  //     const token = localStorage.getItem('matrimony_token');
  //     const adminId = localStorage.getItem('matrimony_userId');
  //     if (!token) {
  //       Swal.fire({
  //         icon: 'error',
  //         title: 'Authentication Error',
  //         text: 'Authentication token not found. Please login again.',
  //         confirmButtonColor: '#3b82f6'
  //       });
  //       return;
  //     }

  //     try {
  //       setUploadingImage(true);

  //       console.log('Uploading image with details:', {
  //         userId,
  //         fileName: file.name,
  //         fileSize: file.size,
  //         fileType: file.type,
  //         field,
  //         section
  //       });

  //       // Create FormData correctly
  //       const formData = new FormData();
  //       formData.append('file', file);

  //       console.log('FormData entries:');
  //       for (let [key, value] of formData.entries()) {
  //         console.log(key, value);
  //       }

  //         const response = await fetch(`${BASE_URL}/document/upload?adminId=${adminId}&type=media`, {

  //           method: 'POST',
  //           headers: {
  //             'Authorization': `Bearer ${token}`,
  //           },
  //           body: formData,
  //         });

  //         if(!response.ok) {
  //           let errorMessage = `Upload failed with status: ${response.status}`;

  //       try {
  //         const errorData = await response.json();
  //         console.log('Error response:', errorData);
  //         errorMessage = errorData.message || errorData.error || errorMessage;
  //       } catch (parseError) {
  //         console.log('Could not parse error response as JSON');
  //         const textError = await response.text();
  //         console.log('Error response text:', textError);
  //         errorMessage = textError || errorMessage;
  //       }

  //       throw new Error(errorMessage);
  //     }

  //       const result = await response.json();
  //     let imageUrl = '';

  //     if (result.imageUrl) {
  //       imageUrl = result.imageUrl;
  //     } else if (result.data?.imageUrl) {
  //       imageUrl = result.data.imageUrl;
  //     } else if (result.url) {
  //       imageUrl = result.url;
  //     } else if (result.data?.url) {
  //       imageUrl = result.data.url;
  //     } else if (result.fileUrl) {
  //       imageUrl = result.fileUrl;
  //     } else if (result.data?.fileUrl) {
  //       imageUrl = result.data.fileUrl;
  //     }

  //     if (imageUrl) {
  //       if (section === 'header') {
  //         setHeaderForm(prev => ({
  //           ...prev,
  //           [field]: imageUrl
  //         }));
  //         if (formErrors[field]) {
  //           setFormErrors(prev => ({ ...prev, [field]: '' }));
  //         }
  //       } else if (section === 'footer') {
  //         setFooterForm(prev => ({
  //           ...prev,
  //           brandInfo: {
  //             ...prev.brandInfo,
  //             [field]: imageUrl
  //           }
  //         }));
  //         if (formErrors.brandLogo) {
  //           setFormErrors(prev => ({ ...prev, brandLogo: '' }));
  //         }
  //       }
  //       Swal.fire({
  //         icon: 'success',
  //         title: 'Success!',
  //         text: 'Image uploaded successfully!',
  //         timer: 2000,
  //         showConfirmButton: false
  //       });
  //     } 
  //     else {
  //       console.warn('No image URL found in response:', result);
  //       throw new Error('No image URL returned in response');
  //     }

  //   } catch (error) {
  //     console.error('Image upload error:', error);

  //     let errorTitle = 'Upload Failed';
  //     let errorText = error.message;

  //     if (error.message.includes('Failed to fetch')) {
  //       errorText = 'Network error: Cannot connect to server. Please check if the server is running.';
  //     } else if (error.message.includes('401')) {
  //       errorText = 'Authentication failed. Please login again.';
  //     } else if (error.message.includes('413')) {
  //       errorText = 'Image file is too large. Please select a smaller image.';
  //     } else if (error.message.includes('415')) {
  //       errorText = 'Unsupported file type. Please use JPEG, PNG, or GIF.';
  //     } else if (error.message.includes('400') && error.message.includes('No file uploaded')) {
  //       errorText = 'Upload failed: Please try selecting the image again.';
  //     }

  //     Swal.fire({
  //       icon: 'error',
  //       title: errorTitle,
  //       text: errorText,
  //       confirmButtonColor: '#3b82f6'
  //     });
  //   } finally {
  //     setUploadingImage(false);
  //   }
  // };

  const handleImageUpload = async (file, field, section) => {
    if (!file) return;

    const token = localStorage.getItem('matrimony_token');
    const adminId = localStorage.getItem('matrimony_userId');

    if (!token) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'Authentication token not found. Please login again.',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    try {
      setUploadingImage(true);

      console.log('Uploading image with details:', {
        userId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        field,
        section
      });

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);

      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      // Upload API Call
      const response = await fetch(
        `${BASE_URL}/document/upload?adminId=${adminId}&type=media`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        let errorMessage = `Upload failed with status: ${response.status}`;

        try {
          const errorData = await response.json();
          console.log('Error response:', errorData);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          const textError = await response.text();
          console.log('Error response text:', textError);
          errorMessage = textError || errorMessage;
        }

        throw new Error(errorMessage);
      }

      // Parse Success Response
      const result = await response.json();
      console.log("Upload result:", result);

      // 🔥 NEW Updated URL extraction based on your response
      // {
      //   statusCode:200,
      //   data:[ { url:"..." } ]
      // }
      let imageUrl = '';
      if (Array.isArray(result.data) && result.data.length > 0 && result.data[0].url) {
        imageUrl = result.data[0].url;
      } else {
        console.warn("No valid image URL found in response:", result);
        throw new Error("No image URL returned in response");
      }

      // 🔥 Apply the updated image to Header / Footer
      if (section === 'header') {
        setHeaderForm(prev => ({
          ...prev,
          [field]: imageUrl
        }));

        if (formErrors[field]) {
          setFormErrors(prev => ({ ...prev, [field]: '' }));
        }
      }

      if (section === 'footer') {
        setFooterForm(prev => ({
          ...prev,
          brandInfo: {
            ...prev.brandInfo,
            [field]: imageUrl
          }
        }));

        if (formErrors.brandLogo) {
          setFormErrors(prev => ({ ...prev, brandLogo: '' }));
        }
      }

      // Success Message
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Image uploaded successfully!',
        timer: 2000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error('Image upload error:', error);

      let errorTitle = 'Upload Failed';
      let errorText = error.message;

      // Friendly error messages
      if (error.message.includes('Failed to fetch')) {
        errorText = 'Network error: Cannot connect to server. Please check if the server is running.';
      } else if (error.message.includes('401')) {
        errorText = 'Authentication failed. Please login again.';
      } else if (error.message.includes('413')) {
        errorText = 'Image file is too large. Please select a smaller image.';
      } else if (error.message.includes('415')) {
        errorText = 'Unsupported file type. Please use JPEG, PNG, or GIF.';
      } else if (error.message.includes('No file uploaded')) {
        errorText = 'Upload failed: Please try selecting the image again.';
      }

      Swal.fire({
        icon: 'error',
        title: errorTitle,
        text: errorText,
        confirmButtonColor: '#3b82f6'
      });

    } finally {
      setUploadingImage(false);
    }
  };


  // Form Validation
  const validateHeaderForm = () => {
    const errors = {};
    if (!headerForm?.heading?.en?.trim()) errors.heading = 'Heading is required';
    if (!headerForm?.subHeading?.en?.trim()) errors.subHeading = 'Subheading is required';
    if (!headerForm.logo.trim()) errors.logo = 'Logo is required';
    if (!headerForm.browseProfiles.trim()) errors.browseProfiles = 'Browse profiles image is required';
    return errors;
  };

  const validateFooterForm = () => {
    const errors = {};

    if (!footerForm?.brandInfo?.brandName?.trim())
      errors.brandName = 'Brand name is required';

    if (!footerForm?.brandInfo?.logo?.trim())
      errors.brandLogo = 'Brand logo is required';

    if (!footerForm?.brandInfo?.tagline?.en?.trim())
      errors.tagline = 'Tagline is required';

    if (!footerForm?.heading?.en?.trim())
      errors.heading = 'Heading is required';

    if (!footerForm?.subHeading?.en?.trim())
      errors.subHeading = 'Subheading is required';

    return errors;
  };

  // Header Operations with SweetAlert
  const createHeader = async () => {
    const errors = validateHeaderForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fix the form errors before submitting.',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        heading: headerForm.heading,
        subHeading: headerForm.subHeading,
        logo: headerForm.logo,
        browseProfiles: headerForm.browseProfiles,
        stats: {
          activeProfiles: headerForm.stats.activeProfiles,
          successStories: headerForm.stats.successStories,
          matchRate: headerForm.stats.matchRate
        }
      };

      const response = await postWithTokenAPI(token, '/header/create', payload);
      if (response?.data) {
        getAllHeader();
        setIsEditing(false);
        setFormErrors({});
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Header created successfully!',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error creating header:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create header. Please try again.',
        confirmButtonColor: '#3b82f6'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateHeader = async () => {
    const errors = validateHeaderForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fix the form errors before submitting.',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        heading: headerForm.heading,
        subHeading: headerForm.subHeading,
        logo: headerForm.logo,
        browseProfiles: headerForm.browseProfiles,
        stats: {
          activeProfiles: headerForm.stats.activeProfiles,
          successStories: headerForm.stats.successStories,
          matchRate: headerForm.stats.matchRate
        }
      };

      const response = await putWithTokenAPI(token, `/header/update?id=${headerData._id}`, payload);
      if (response?.data) {
        getAllHeader();
        setIsEditing(false);
        setFormErrors({});
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Header updated successfully!',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error updating header:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update header. Please try again.',
        confirmButtonColor: '#3b82f6'
      });
    } finally {
      setLoading(false);
    }
  };

  // Footer Operations with SweetAlert
  const createFooter = async () => {
    // const errors = validateFooterForm();
    // if (Object.keys(errors).length > 0) {
    //   setFormErrors(errors);
    //   Swal.fire({
    //     icon: 'error',
    //     title: 'Validation Error',
    //     text: 'Please fix the form errors before submitting.',
    //     confirmButtonColor: '#3b82f6'
    //   });
    //   return;
    // }

    try {
      setLoading(true);
      const payload = {
        brandInfo: {
          brandName: footerForm.brandInfo.brandName,
          logo: footerForm.brandInfo.logo,
          tagline: footerForm.brandInfo.tagline,
          yearsActive: footerForm.brandInfo.yearsActive
        },
        heading: footerForm.heading,
        subHeading: footerForm.subHeading,
        socialMedia: {
          facebook: footerForm.socialMedia.facebook,
          twitter: footerForm.socialMedia.twitter,
          instagram: footerForm.socialMedia.instagram,
          youtube: footerForm.socialMedia.youtube,
          threads: footerForm.socialMedia.threads
        },
        contactInfo: {
          email: footerForm.contactInfo.email,
          phone: footerForm.contactInfo.phone,
          address: footerForm.contactInfo.address
        },
        is_facebook_active: footerForm.is_facebook_active,
        is_twitter_active: footerForm.is_twitter_active,
        is_instagram_active: footerForm.is_instagram_active,
        is_youtube_active: footerForm.is_youtube_active,
        is_threads_active: footerForm.is_threads_active
      };

      console.log('Creating footer with activation states:', payload);

      const response = await postWithTokenAPI(token, '/footer/create', payload);
      if (response?.data) {
        getAllFooter();
        setIsEditing(false);
        setFormErrors({});
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Footer created successfully!',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error creating footer:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create footer. Please try again.',
        confirmButtonColor: '#3b82f6'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFooter = async () => {
    const errors = validateFooterForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fix the form errors before submitting.',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    try {
      setLoading(true);

      // DEBUG: Log what we're sending to backend
      console.log('Updating footer with activation states:', {
        is_facebook_active: footerForm.is_facebook_active,
        is_twitter_active: footerForm.is_twitter_active,
        is_instagram_active: footerForm.is_instagram_active,
        is_youtube_active: footerForm.is_youtube_active,
        is_threads_active: footerForm.is_threads_active
      });

      const payload = {
        brandInfo: {
          brandName: footerForm.brandInfo.brandName,
          logo: footerForm.brandInfo.logo,
          tagline: footerForm.brandInfo.tagline,
          yearsActive: footerForm.brandInfo.yearsActive
        },
        heading: footerForm.heading,
        subHeading: footerForm.subHeading,
        socialMedia: {
          facebook: footerForm.socialMedia.facebook,
          twitter: footerForm.socialMedia.twitter,
          instagram: footerForm.socialMedia.instagram,
          youtube: footerForm.socialMedia.youtube,
          threads: footerForm.socialMedia.threads
        },
        EmailAddress: footerForm.contactInfo.email,
        PhoneNumber: footerForm.contactInfo.phone,
        OfficeAddress: footerForm.contactInfo.address,
        is_facebook_active: footerForm.is_facebook_active,
        is_twitter_active: footerForm.is_twitter_active,
        is_instagram_active: footerForm.is_instagram_active,
        is_youtube_active: footerForm.is_youtube_active,
        is_threads_active: footerForm.is_threads_active
      };

      const response = await putWithTokenAPI(token, `/footer/update?id=${footerData._id}`, payload);

      // DEBUG: Log the response
      console.log('Update footer response:', response);

      if (response?.data) {
        // Refresh the data to ensure sync
        await getAllFooter();
        setIsEditing(false);
        setFormErrors({});
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Footer updated successfully!',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error updating footer:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update footer. Please try again.',
        confirmButtonColor: '#3b82f6'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateTerms = async () => {
    try {
      setLoading(true);
      const payload = {
        title: termsForm.title,
        content: termsForm.content,
        is_deleted: termsForm.is_deleted
      };
      const response = await putWithTokenAPI(token, `/terms-condition/update?id=${termsData._id}`, payload);
      if (response?.data || response?.statusCode === 200) {
        getTerms();
        setIsEditing(false);
        Swal.fire({ icon: 'success', title: 'Success!', text: 'Terms updated successfully!', timer: 2000, showConfirmButton: false });
      }
    } catch (error) {
      console.error('Error updating terms:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update terms.' });
    } finally {
      setLoading(false);
    }
  };

  const updatePrivacy = async () => {
    try {
      setLoading(true);
      const payload = {
        title: privacyForm.title,
        content: privacyForm.content,
        is_deleted: privacyForm.is_deleted
      };
      const response = await putWithTokenAPI(token, `/privacy-policy/update?id=${privacyData._id}`, payload);
      if (response?.data || response?.statusCode === 200) {
        getPrivacy();
        setIsEditing(false);
        Swal.fire({ icon: 'success', title: 'Success!', text: 'Privacy Policy updated successfully!', timer: 2000, showConfirmButton: false });
      }
    } catch (error) {
      console.error('Error updating privacy:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update privacy policy.' });
    } finally {
      setLoading(false);
    }
  };

  const updateCancellation = async () => {
    try {
      setLoading(true);
      const payload = {
        title: cancellationForm.title,
        content: cancellationForm.content,
        is_deleted: cancellationForm.is_deleted
      };
      const response = await putWithTokenAPI(token, `/cancellation-refund-policy/update?id=${cancellationData._id}`, payload);
      if (response?.data || response?.statusCode === 200) {
        getCancellation();
        setIsEditing(false);
        Swal.fire({ icon: 'success', title: 'Success!', text: 'Cancellation & Refund Policy updated successfully!', timer: 2000, showConfirmButton: false });
      }
    } catch (error) {
      console.error('Error updating cancellation:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update cancellation policy.' });
    } finally {
      setLoading(false);
    }
  };

  const updateShipping = async () => {
    try {
      setLoading(true);
      const payload = {
        title: shippingForm.title,
        content: shippingForm.content,
        is_deleted: shippingForm.is_deleted
      };
      const response = await putWithTokenAPI(token, `/shipping-exchange-policy/update?id=${shippingData._id}`, payload);
      if (response?.data || response?.statusCode === 200) {
        getShipping();
        setIsEditing(false);
        Swal.fire({ icon: 'success', title: 'Success!', text: 'Shipping & Exchange Policy updated successfully!', timer: 2000, showConfirmButton: false });
      }
    } catch (error) {
      console.error('Error updating shipping:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update shipping policy.' });
    } finally {
      setLoading(false);
    }
  };

  // Input Handlers
  // const handleHeaderInputChange = (field, value) => {
  //   setHeaderForm(prev => ({
  //     ...prev,
  //     [field]: value
  //   }));
  //   if (formErrors[field]) {
  //     setFormErrors(prev => ({ ...prev, [field]: '' }));
  //   }
  // };

  // const handleHeaderInputChange = (path, value) => {
  //   setHeaderForm((prev) => {
  //     const updated = JSON.parse(JSON.stringify(prev)); // deep copy
  //     const keys = path.split("."); // example: ['heading', 'en']

  //     let obj = updated;
  //     for (let i = 0; i < keys.length - 1; i++) {
  //       obj = obj[keys[i]]; // go inside nested object
  //     }

  //     obj[keys[keys.length - 1]] = value; // update final key

  //     return updated;
  //   });
  // };

  const handleHeaderInputChange = (path, value) => {
    setHeaderForm((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");

      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];

        // If parent is not an object → convert it to object
        if (typeof obj[key] !== "object" || obj[key] === null) {
          obj[key] = {}; // Create missing nested object
        }

        obj = obj[key];
      }

      obj[keys[keys.length - 1]] = value;

      return updated;
    });
  };


  const handleHeaderStatsChange = (field, value) => {
    setHeaderForm(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        [field]: value
      }
    }));
  };

  const handleFooterInputChange = (field, value) => {
    setFooterForm(prev => ({
      ...prev,
      [field]: value
    }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };


  const handleFooterBrandInfoChange = (path, value) => {
    setFooterForm(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = updated.brandInfo;

      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          obj[key] = value;               // final assign
        } else {
          if (typeof obj[key] !== "object" || obj[key] === null) {
            obj[key] = {};                // Create object if it doesn't exist
          }
          obj = obj[key];
        }
      });

      return updated;
    });

    if (formErrors[path]) {
      setFormErrors(prev => ({ ...prev, [path]: "" }));
    }
  };


  const handleFooterSocialMediaChange = (platform, value) => {
    setFooterForm(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: value
      }
    }));
  };

  const handleFooterContactInfoChange = (path, value) => {
    setFooterForm(prev => {
      const updated = structuredClone(prev); // safer deep clone

      const keys = path.split("."); // ['address','en']
      let obj = updated.contactInfo;

      for (let i = 0; i < keys.length - 1; i++) {
        obj = obj[keys[i]];
      }

      obj[keys[keys.length - 1]] = value; // update last key

      return updated;
    });
  };


  // Social Media Activation Handlers - FIXED
  const handleSocialMediaActivationChange = (field, value) => {
    console.log(`Changing ${field} to:`, value);
    setFooterForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Monitor state changes
  useEffect(() => {
    console.log('Footer form activation states updated:', {
      is_facebook_active: footerForm.is_facebook_active,
      is_twitter_active: footerForm.is_twitter_active,
      is_instagram_active: footerForm.is_instagram_active,
      is_youtube_active: footerForm.is_youtube_active,
      is_threads_active: footerForm.is_threads_active
    });
  }, [
    footerForm.is_facebook_active,
    footerForm.is_twitter_active,
    footerForm.is_instagram_active,
    footerForm.is_youtube_active,
    footerForm.is_threads_active
  ]);

  useEffect(() => {
    getAllHeader();
    getAllFooter();
    getTerms();
    getPrivacy();
    getCancellation();
    getShipping();
  }, []);
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            {/* <Button
              variant="outline"
              onClick={handleBack}
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 cursor-pointer" />
              Back
            </Button> */}
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t("content.planName")}</h1>
          <p className="text-gray-600 mt-2">{t("content.duration")}</p>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={handleMainTabChange} className="space-y-6">
          <TabsList className="inline-flex flex-wrap w-auto border border-gray-200 rounded-lg p-1 bg-muted  gap-1">
            <TabsTrigger value="header" className={`cursor-pointer px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'header' ? '!bg-red-600 !text-white shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`}>
              {t("content.header")}{headerData ? ' ✓' : ''}
            </TabsTrigger>
            <TabsTrigger value="footer" className={`cursor-pointer px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'footer' ? '!bg-red-600 !text-white shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`}>
              {t("content.footer")}{footerData ? ' ✓' : ''}
            </TabsTrigger>
            <TabsTrigger value="terms" className={`cursor-pointer px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'terms' ? '!bg-red-600 !text-white shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`}>
              Terms &amp; Conditions{termsData ? ' ✓' : ''}
            </TabsTrigger>
            <TabsTrigger value="privacy" className={`cursor-pointer px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'privacy' ? '!bg-red-600 !text-white shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`}>
              Privacy Policy{privacyData ? ' ✓' : ''}
            </TabsTrigger>
            <TabsTrigger value="cancellation" className={`cursor-pointer px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'cancellation' ? '!bg-red-600 !text-white shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`}>
              Cancellation &amp; Refund{cancellationData ? ' ✓' : ''}
            </TabsTrigger>
            <TabsTrigger value="shipping" className={`cursor-pointer px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'shipping' ? '!bg-red-600 !text-white shadow-sm' : 'hover:bg-gray-100 text-gray-700'}`}>
              Shipping &amp; Exchange{shippingData ? ' ✓' : ''}
            </TabsTrigger>
          </TabsList>

          {/* Header Tab */}
          <TabsContent value="header" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  {t("content.headerConfiguration")}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {headerData ? (
                    <>
                      <Button
                        variant={isEditing ? "outline" : "default"}
                        onClick={() => setIsEditing(!isEditing)}
                        className="px-6 py-2 text-white rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2 cursor-pointer"
                        style={{
                          background:
                            "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))",
                        }} >
                        <Edit className="w-4 h-4" />
                        {isEditing ? t("content.cancelEdit") : t("content.editHeader")}
                      </Button>
                    </>
                  ) : (
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                      {t("content.noHeader")}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {activeTab === "header" && (
                  <Tabs value={headerActiveTab} onValueChange={handleHeaderSubTabChange} className="space-y-6">
                    <TabsList className="flex w-full max-w-2xl">
                      <TabsTrigger value="main-content" className="flex items-center gap-2 cursor-pointer flex-1">
                        <MessageCircle className="w-4 h-4" />
                        {t("content.mainContent")}
                      </TabsTrigger>
                      <TabsTrigger value="header-logo" className="flex items-center gap-2 cursor-pointer flex-1">
                        <ImageIcon className="w-4 h-4" />
                        {t("content.headerLogo")}
                      </TabsTrigger>
                      <TabsTrigger value="browse-profiles" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Users className="w-4 h-4" />
                        {t("content.browseProfiles")}
                      </TabsTrigger>
                      <TabsTrigger value="statistics" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Target className="w-4 h-4" />
                        {t("content.statistics")}
                      </TabsTrigger>
                    </TabsList>

                    {/* Main Content Tab */}
                    <TabsContent value="main-content" className="space-y-6">
                      <div className="grid grid-cols-2 gap-6">

                        <div>
                          <div>
                            <Label htmlFor="heading" className="flex items-center gap-2 mb-2">
                              {t("content.mainHeading")} <span style={{ color: 'red' }}>*</span>(English)
                            </Label>
                            <Input
                              id="heading"
                              value={headerForm.heading.en}
                              onChange={(e) => handleHeaderInputChange('heading.en', e.target.value)}
                              placeholder="Enter main heading..."
                              className={`w-full ${formErrors.heading
                                ? "border-red-500"
                                : isEditing
                                  ? inputActiveClass
                                  : inputDisabledClass
                                }`}
                              disabled={headerData && !isEditing}
                            />
                            {formErrors.heading && (
                              <p className="text-red-500 text-sm mt-1">{formErrors.heading}</p>
                            )}
                          </div>
                          <div className='mt-3'>
                            <Label htmlFor="subHeading" className="flex items-center gap-2 mb-2">
                              {t("content.subHeading")} <span style={{ color: 'red' }}>*</span>(English)
                            </Label>
                            <textarea
                              id="subHeading"
                              value={headerForm.subHeading.en}
                              onChange={(e) => handleHeaderInputChange('subHeading.en', e.target.value)}
                              placeholder="Enter subheading (this will appear smaller than the main heading)..."
                              rows={3}
                              className={`w-full px-3 py-2 border rounded-md resize-none text-sm ${formErrors.subHeading
                                ? "border-red-500"
                                : isEditing
                                  ? inputActiveClass
                                  : inputDisabledClass
                                }`}
                              disabled={headerData && !isEditing}
                            />
                            {formErrors.subHeading && (
                              <p className="text-red-500 text-sm mt-1">{formErrors.subHeading}</p>
                            )}
                          </div>
                        </div>


                        <div>
                          <div>
                            <Label htmlFor="heading" className="flex items-center gap-2 mb-2">
                              {t("content.mainHeading")}<span style={{ color: 'red' }}>*</span> (Tamil)
                            </Label>
                            <Input
                              id="heading"
                              value={headerForm.heading.ta}
                              onChange={(e) => handleHeaderInputChange('heading.ta', e.target.value)}
                              placeholder="Enter main heading..."
                              className={`w-full ${formErrors.heading
                                ? "border-red-500"
                                : isEditing
                                  ? inputActiveClass
                                  : inputDisabledClass
                                }`}
                              disabled={headerData && !isEditing}
                            />
                            {formErrors.heading && (
                              <p className="text-red-500 text-sm mt-1">{formErrors.heading}</p>
                            )}
                          </div>
                          <div className='mt-3'>
                            <Label htmlFor="subHeading" className="flex items-center gap-2 mb-2">
                              {t("content.subHeading")} <span style={{ color: 'red' }}>*</span>(Tamil)
                            </Label>
                            <textarea
                              id="subHeading"
                              value={headerForm.subHeading.ta}
                              onChange={(e) => handleHeaderInputChange('subHeading.ta', e.target.value)}
                              placeholder="Enter subheading (this will appear smaller than the main heading)..."
                              rows={3}
                              className={`w-full px-3 py-2 border rounded-md resize-none text-sm ${formErrors.subHeading
                                ? "border-red-500"
                                : isEditing
                                  ? inputActiveClass
                                  : inputDisabledClass
                                }`}
                              disabled={headerData && !isEditing}
                            />
                            {formErrors.subHeading && (
                              <p className="text-red-500 text-sm mt-1">{formErrors.subHeading}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Header Logo Tab */}
                    <TabsContent value="header-logo" className="space-y-6">
                      <div className="space-y-4">
                        {headerForm.logo && (
                          <div className="flex flex-col items-start space-y-2">
                            <img
                              src={headerForm.logo}
                              alt="Header Logo Preview"
                              className="w-16 h-16 object-contain rounded border border-gray-300 bg-white shadow-sm"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleImagePreview(headerForm.logo, 'Header Logo')}
                              className="flex items-center gap-1 text-xs cursor-pointer"
                            >
                              <Eye className="w-3 h-3 cursor-pointer" />
                              Preview
                            </Button>
                          </div>
                        )}
                        <div className="space-y-3">

                          <div className="flex justify-start">
                            <input
                              type="file"
                              id="header-logo-upload"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e.target.files[0], 'logo', 'header')}
                              className="hidden"
                              disabled={(headerData && !isEditing) || uploadingImage}
                            />
                            <Label
                              htmlFor="header-logo-upload"
                              className={`inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg text-sm cursor-pointer shadow-sm transition-colors ${isEditing && !uploadingImage
                                ? "border-blue-400 bg-blue-50 text-blue-600 hover:bg-blue-100"
                                : "border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                                }`}
                            >
                              <Upload className="w-4 h-4" />
                              {uploadingImage ? 'Uploading...' : (headerForm.logo ? 'Change Logo' : 'Upload Logo')}
                            </Label>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Browse Profiles Tab */}
                    <TabsContent value="browse-profiles" className="space-y-6">
                      <div className="space-y-4">
                        {headerForm.browseProfiles && (
                          <div className="flex flex-col items-start space-y-2">
                            <img
                              src={headerForm.browseProfiles}
                              alt="Browse Profiles Preview"
                              className="w-16 h-16 object-contain rounded border border-gray-300 bg-white shadow-sm"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleImagePreview(headerForm.browseProfiles, 'Browse Profiles Image')}
                              className="flex items-center gap-1 text-xs cursor-pointer"
                            >
                              <Eye className="w-3 h-3 cursor-pointer" />
                              {t("content.preview")}
                            </Button>
                          </div>
                        )}
                        <div className="space-y-3">
                          {/* <div>
                          <Label htmlFor="browseProfilesUrl" className="flex items-center gap-2 mb-2">
                            {t("content.browseProfilesUrl")} <span style={{ color: 'red' }}>*</span>
                          </Label>
                          <Input
                            id="browseProfilesUrl"
                            value={headerForm.browseProfiles}
                            onChange={(e) => handleHeaderInputChange('browseProfiles', e.target.value)}
                            placeholder="Enter browse profiles image URL..."
                            className={`w-full ${formErrors.browseProfiles ? 'border-red-500' : isEditing ? inputActiveClass : inputDisabledClass}`}
                            disabled={headerData && !isEditing}
                          />
                          {formErrors.browseProfiles && (
                            <p className="text-red-500 text-sm mt-1">{formErrors.browseProfiles}</p>
                          )}
                        </div> */}
                          <div className="flex justify-start">
                            <input
                              type="file"
                              id="browse-upload"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e.target.files[0], 'browseProfiles', 'header')}
                              className="hidden"
                              disabled={(headerData && !isEditing) || uploadingImage}
                            />
                            <Label
                              htmlFor="browse-upload"
                              className={`inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg text-sm cursor-pointer shadow-sm transition-colors ${isEditing && !uploadingImage
                                ? "border-blue-400 bg-blue-50 text-blue-600 hover:bg-blue-100"
                                : "border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                                }`}
                            >
                              <Upload className="w-4 h-4 cursor-pointer" />
                              {uploadingImage ? t("content.uploading") : (headerForm.browseProfiles ? t("content.changeImage") : t("content.uploadImage"))}
                            </Label>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Statistics Tab */}
                    <TabsContent value="statistics" className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="activeProfiles" className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4" />
                            {t("content.activeProfiles")}
                          </Label>
                          <Input
                            id="activeProfiles"
                            value={headerForm.stats.activeProfiles}
                            onChange={(e) => handleHeaderStatsChange('activeProfiles', e.target.value)}
                            placeholder="e.g., 5000+"
                            className={isEditing ? inputActiveClass : inputDisabledClass}
                            disabled={headerData && !isEditing}
                          />
                        </div>
                        <div>
                          <Label htmlFor="successStories" className="flex items-center gap-2 mb-2">
                            <Heart className="w-4 h-4" />
                            {t("content.successStories")}
                          </Label>
                          <Input
                            id="successStories"
                            value={headerForm.stats.successStories}
                            onChange={(e) => handleHeaderStatsChange('successStories', e.target.value)}
                            placeholder="e.g., 1000+"
                            className={isEditing ? inputActiveClass : inputDisabledClass}
                            disabled={headerData && !isEditing}
                          />
                        </div>
                        <div>
                          <Label htmlFor="matchRate" className="flex items-center gap-2 mb-2">
                            <Target className="w-4 h-4" />
                            {t("content.matchRate")}
                          </Label>
                          <Input
                            id="matchRate"
                            value={headerForm.stats.matchRate}
                            onChange={(e) => handleHeaderStatsChange('matchRate', e.target.value)}
                            placeholder="e.g., 95%"
                            className={isEditing ? inputActiveClass : inputDisabledClass}
                            disabled={headerData && !isEditing}
                          />
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                )}

                {/* Header Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t">
                  {!headerData ? (
                    <Button
                      onClick={createHeader}
                      disabled={loading}
                      className="flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {loading ? 'Creating...' : 'Create Header'}
                    </Button>
                  ) : isEditing && (
                    <Button
                      onClick={updateHeader}
                      disabled={loading}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Save className="w-4 h-4 cursor-pointer" />
                      {loading ? 'Updating...' : 'Update Header'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Footer Tab */}
          <TabsContent value="footer" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  {t("content.footerConfiguration")}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {footerData ? (
                    <>
                      <Button
                        variant={isEditing ? "outline" : "default"}
                        onClick={() => setIsEditing(!isEditing)}
                        className="px-6 py-2 text-white rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2 cursor-pointer"
                        style={{
                          background:
                            "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))",
                        }}>
                        <Edit className="w-4 h-4 cursor-pointer" />
                        {isEditing ? t("content.cancelEdit") : t("content.editFooter")}
                      </Button>
                    </>
                  ) : (
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                      {t("content.noFooter")}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Footer Sub Tabs - Only show when footer is active */}
                {activeTab === "footer" && (
                  <Tabs value={footerActiveTab} onValueChange={handleFooterSubTabChange} className="space-y-6">
                    <TabsList className="flex w-full max-w-3xl">
                      <TabsTrigger value="brand-info" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Building className="w-4 h-4" />
                        {t("content.brandInfo")}
                      </TabsTrigger>
                      {/* <TabsTrigger value="call-to-action" className="flex items-center gap-2 cursor-pointer flex-1">
                        <MessageCircle className="w-4 h-4" />
                        {t("content.callToAction")}
                      </TabsTrigger> */}
                      <TabsTrigger value="social-media" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Link className="w-4 h-4" />
                        {t("content.socialMedia")}
                      </TabsTrigger>
                      <TabsTrigger value="contact-info" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Phone className="w-4 h-4" />
                        {t("content.contactInfo")}
                      </TabsTrigger>
                      <TabsTrigger value="brand-logo" className="flex items-center gap-2 cursor-pointer flex-1">
                        <ImageIcon className="w-4 h-4" />
                        {t("content.brandLogo")}
                      </TabsTrigger>
                    </TabsList>

                    {/* Brand Information Tab */}
                    <TabsContent value="brand-info" className="space-y-6">
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="brandName" className="flex items-center gap-2 mb-2">
                            {t("content.brandName")}<span style={{ color: 'red' }}>*</span>
                          </Label>
                          <Input
                            id="brandName"
                            value={footerForm.brandInfo.brandName}
                            onChange={(e) => handleFooterBrandInfoChange('brandName', e.target.value)}
                            placeholder="Enter brand name..."
                            className={`w-full ${formErrors.brandName ? 'border-red-500' : isEditing ? inputActiveClass : inputDisabledClass}`}
                            disabled={footerData && !isEditing}
                          />
                          {formErrors.brandName && (
                            <p className="text-red-500 text-sm mt-1">{formErrors.brandName}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                          <div>
                            <Label htmlFor="tagline" className="flex items-center gap-2 mb-2">
                              {t("content.tagline")} <span style={{ color: 'red' }}>*</span>(English)
                            </Label>
                            <textarea
                              id="tagline"
                              value={footerForm?.brandInfo?.tagline?.en}
                              onChange={(e) => handleFooterBrandInfoChange('tagline.en', e.target.value)}
                              placeholder="Enter brand tagline..."
                              rows={2}
                              className={`w-full px-3 py-2 border rounded-md resize-none text-sm ${formErrors.tagline
                                ? "border-red-500"
                                : isEditing
                                  ? inputActiveClass
                                  : inputDisabledClass
                                }`}
                              disabled={footerData && !isEditing}
                            />
                            {formErrors.tagline && (
                              <p className="text-red-500 text-sm mt-1">{formErrors.tagline}</p>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="tagline" className="flex items-center gap-2 mb-2">
                              {t("content.tagline")} <span style={{ color: 'red' }}>*</span>(Tamil)
                            </Label>
                            <textarea
                              id="tagline"
                              value={footerForm.brandInfo.tagline.ta}
                              onChange={(e) => handleFooterBrandInfoChange('tagline.ta', e.target.value)}
                              placeholder="Enter brand tagline..."
                              rows={2}
                              className={`w-full px-3 py-2 border rounded-md resize-none text-sm ${formErrors.tagline
                                ? "border-red-500"
                                : isEditing
                                  ? inputActiveClass
                                  : inputDisabledClass
                                }`}
                              disabled={footerData && !isEditing}
                            />
                            {formErrors.tagline && (
                              <p className="text-red-500 text-sm mt-1">{formErrors.tagline}</p>
                            )}
                          </div>
                        </div>


                        {/* <div>
                          <Label htmlFor="yearsActive" className="flex items-center gap-2 mb-2">
                            Years Active
                          </Label>
                          <Input
                            id="yearsActive"
                            value={footerForm.brandInfo.yearsActive}
                            onChange={(e) => handleFooterBrandInfoChange('yearsActive', e.target.value)}
                            placeholder="e.g., 12"
                            className={isEditing ? inputActiveClass : inputDisabledClass}
                            disabled={footerData && !isEditing}
                          />
                        </div> */}
                      </div>
                    </TabsContent>

                    {/* Call to Action Tab */}
                    <TabsContent value="call-to-action" className="space-y-6">
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="footerHeading" className="flex items-center gap-2 mb-2">
                            {t("content.heading")} <span style={{ color: 'red' }}>*</span>
                          </Label>
                          <Input
                            id="footerHeading"
                            value={footerForm.heading}
                            onChange={(e) => handleFooterInputChange('heading', e.target.value)}
                            placeholder="Enter footer heading..."
                            className={`w-full ${formErrors.heading ? 'border-red-500' : isEditing ? inputActiveClass : inputDisabledClass}`}
                            disabled={footerData && !isEditing}
                          />
                          {formErrors.heading && (
                            <p className="text-red-500 text-sm mt-1">{formErrors.heading}</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="footerSubHeading" className="flex items-center gap-2 mb-2">
                            {t("subscription.")}  <span style={{ color: 'red' }}>*</span>
                          </Label>
                          <textarea
                            id="footerSubHeading"
                            value={footerForm.subHeading}
                            onChange={(e) => handleFooterInputChange('subHeading', e.target.value)}
                            placeholder="Enter footer subheading (this will appear smaller than the main heading)..."
                            rows={2}
                            className={`w-full px-3 py-2 border rounded-md resize-none text-sm ${formErrors.subHeading
                              ? 'border-red-500'
                              : isEditing
                                ? inputActiveClass
                                : inputDisabledClass
                              }`}
                            disabled={footerData && !isEditing}
                          />
                          {formErrors.subHeading && (
                            <p className="text-red-500 text-sm mt-1">{formErrors.subHeading}</p>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="social-media" className="space-y-6">
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <Link className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800">{t("content.socialMediaPlatforms")}</h4>
                            <p className="text-sm text-gray-600">
                              {isEditing
                                ? "Toggle each platform on/off using the switch buttons. Enter URLs for active platforms. Changes will be saved when you click 'Update Footer'."
                                : "View social media platform URLs. Click 'Edit Footer' to make changes."
                              }
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Facebook */}
                        <div className={`space-y-3 p-4 rounded-lg border-2 ${footerForm.is_facebook_active
                          ? 'bg-green-50 border-green-300 shadow-sm'
                          : 'bg-gray-50 border-gray-200'
                          } transition-all duration-200`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Facebook className={`w-5 h-5 ${footerForm.is_facebook_active ? 'text-blue-600' : 'text-gray-500'
                                }`} />
                              <Label htmlFor="facebook" className="text-sm font-semibold">
                                {t("content.facebook")}
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${footerForm.is_facebook_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {footerForm.is_facebook_active ? 'Active' : 'Inactive'}
                              </span>
                              {/* Only show toggle button when editing */}
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleSocialMediaActivationChange('is_facebook_active', !footerForm.is_facebook_active)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${footerForm.is_facebook_active
                                    ? 'bg-green-600 shadow-lg shadow-green-200'
                                    : 'bg-red-500 shadow-lg shadow-red-200'
                                    } cursor-pointer hover:scale-105`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-all duration-300 ${footerForm.is_facebook_active ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                          <Input
                            id="facebook"
                            value={footerForm.socialMedia.facebook}
                            onChange={(e) => handleFooterSocialMediaChange('facebook', e.target.value)}
                            placeholder="https://facebook.com/yourpage"
                            className={`mt-2 ${isEditing && footerForm.is_facebook_active ? inputActiveClass : inputDisabledClass}`}
                            disabled={!isEditing || !footerForm.is_facebook_active}
                          />
                        </div>

                        {/* Twitter */}
                        <div className={`space-y-3 p-4 rounded-lg border-2 ${footerForm.is_twitter_active
                          ? 'bg-green-50 border-green-300 shadow-sm'
                          : 'bg-gray-50 border-gray-200'
                          } transition-all duration-200`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Twitter className={`w-5 h-5 ${footerForm.is_twitter_active ? 'text-blue-400' : 'text-gray-500'
                                }`} />
                              <Label htmlFor="twitter" className="text-sm font-semibold">
                                {t("content.twitter")}
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${footerForm.is_twitter_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {footerForm.is_twitter_active ? 'Active' : 'Inactive'}
                              </span>
                              {/* Only show toggle button when editing */}
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleSocialMediaActivationChange('is_twitter_active', !footerForm.is_twitter_active)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${footerForm.is_twitter_active
                                    ? 'bg-green-600 shadow-lg shadow-green-200'
                                    : 'bg-red-500 shadow-lg shadow-red-200'
                                    } cursor-pointer hover:scale-105`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-all duration-300 ${footerForm.is_twitter_active ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                          <Input
                            id="twitter"
                            value={footerForm.socialMedia.twitter}
                            onChange={(e) => handleFooterSocialMediaChange('twitter', e.target.value)}
                            placeholder="https://twitter.com/yourpage"
                            className={`mt-2 ${isEditing && footerForm.is_twitter_active ? inputActiveClass : inputDisabledClass}`}
                            disabled={!isEditing || !footerForm.is_twitter_active}
                          />
                        </div>

                        {/* Instagram */}
                        <div className={`space-y-3 p-4 rounded-lg border-2 ${footerForm.is_instagram_active
                          ? 'bg-green-50 border-green-300 shadow-sm'
                          : 'bg-gray-50 border-gray-200'
                          } transition-all duration-200`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Instagram className={`w-5 h-5 ${footerForm.is_instagram_active ? 'text-pink-600' : 'text-gray-500'
                                }`} />
                              <Label htmlFor="instagram" className="text-sm font-semibold">
                                {t("content.instagram")}
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${footerForm.is_instagram_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {footerForm.is_instagram_active ? 'Active' : 'Inactive'}
                              </span>
                              {/* Only show toggle button when editing */}
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleSocialMediaActivationChange('is_instagram_active', !footerForm.is_instagram_active)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${footerForm.is_instagram_active
                                    ? 'bg-green-600 shadow-lg shadow-green-200'
                                    : 'bg-red-500 shadow-lg shadow-red-200'
                                    } cursor-pointer hover:scale-105`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-all duration-300 ${footerForm.is_instagram_active ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                          <Input
                            id="instagram"
                            value={footerForm.socialMedia.instagram}
                            onChange={(e) => handleFooterSocialMediaChange('instagram', e.target.value)}
                            placeholder="https://instagram.com/yourpage"
                            className={`mt-2 ${isEditing && footerForm.is_instagram_active ? inputActiveClass : inputDisabledClass}`}
                            disabled={!isEditing || !footerForm.is_instagram_active}
                          />
                        </div>

                        {/* YouTube */}
                        <div className={`space-y-3 p-4 rounded-lg border-2 ${footerForm.is_youtube_active
                          ? 'bg-green-50 border-green-300 shadow-sm'
                          : 'bg-gray-50 border-gray-200'
                          } transition-all duration-200`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Youtube className={`w-5 h-5 ${footerForm.is_youtube_active ? 'text-red-600' : 'text-gray-500'
                                }`} />
                              <Label htmlFor="youtube" className="text-sm font-semibold">
                                {t("content.youtube")}
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${footerForm.is_youtube_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {footerForm.is_youtube_active ? 'Active' : 'Inactive'}
                              </span>
                              {/* Only show toggle button when editing */}
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleSocialMediaActivationChange('is_youtube_active', !footerForm.is_youtube_active)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${footerForm.is_youtube_active
                                    ? 'bg-green-600 shadow-lg shadow-green-200'
                                    : 'bg-red-500 shadow-lg shadow-red-200'
                                    } cursor-pointer hover:scale-105`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-all duration-300 ${footerForm.is_youtube_active ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                          <Input
                            id="youtube"
                            value={footerForm.socialMedia.youtube}
                            onChange={(e) => handleFooterSocialMediaChange('youtube', e.target.value)}
                            placeholder="https://youtube.com/yourchannel"
                            className={`mt-2 ${isEditing && footerForm.is_youtube_active ? inputActiveClass : inputDisabledClass}`}
                            disabled={!isEditing || !footerForm.is_youtube_active}
                          />
                        </div>

                        {/* Threads */}
                        <div className={`space-y-3 p-4 rounded-lg border-2 ${footerForm.is_threads_active
                          ? 'bg-green-50 border-green-300 shadow-sm'
                          : 'bg-gray-50 border-gray-200'
                          } transition-all duration-200`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {/* Threads SVG icon (no lucide-react icon yet) */}
                              <svg
                                className={`w-5 h-5 ${footerForm.is_threads_active ? 'text-gray-900' : 'text-gray-400'}`}
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.473 12.01v-.017c.027-3.579.875-6.43 2.522-8.475C5.845 1.207 8.6.027 12.18 0h.014c2.746.019 5.043.725 6.826 2.098 1.677 1.29 2.839 3.155 3.443 5.544l-2.344.558c-.99-4.145-3.477-6.25-7.925-6.279-2.885.018-5.1.926-6.584 2.697-1.384 1.653-2.084 4.013-2.108 7.02.024 3.006.725 5.364 2.108 7.017 1.485 1.771 3.7 2.678 6.584 2.696 2.75-.017 4.728-.794 6.068-2.373 1.022-1.216 1.613-2.967 1.774-5.235a11.17 11.17 0 0 0-.071-2.107c-.24-1.526-.854-2.719-1.826-3.555-.638-.554-1.395-.903-2.27-1.025.008.277.013.558.013.845 0 3.02-1.086 5.285-3.132 6.565-1.54.957-3.451 1.26-5.499.891-1.683-.306-3.048-1.025-3.957-2.079-.835-.97-1.271-2.215-1.271-3.606 0-2.966 1.869-5.108 5.064-5.97 1.47-.393 3.127-.522 4.93-.375.18.014.358.03.534.049-.103-.553-.27-1.003-.506-1.346-.384-.558-1.002-.877-1.885-.949-1.277-.103-2.373.214-3.228.902-.759.617-1.186 1.478-1.271 2.556l-2.372-.2c.127-1.658.821-3.012 2.066-4.024 1.23-.998 2.874-1.49 4.895-1.322 1.535.124 2.712.648 3.503 1.558.705.816 1.078 1.913 1.134 3.28.028.712.013 1.367-.046 1.95.447.201.87.457 1.269.769 1.411 1.105 2.285 2.787 2.65 5.013.11.663.162 1.4.155 2.188-.186 2.85-.926 5.093-2.213 6.673-1.76 2.115-4.427 3.192-7.923 3.208Z" />
                              </svg>
                              <Label htmlFor="threads" className="text-sm font-semibold">
                                Threads
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${footerForm.is_threads_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {footerForm.is_threads_active ? 'Active' : 'Inactive'}
                              </span>
                              {/* Only show toggle button when editing */}
                              {isEditing && (
                                <button
                                  type="button"
                                  onClick={() => handleSocialMediaActivationChange('is_threads_active', !footerForm.is_threads_active)}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${footerForm.is_threads_active
                                    ? 'bg-green-600 shadow-lg shadow-green-200'
                                    : 'bg-red-500 shadow-lg shadow-red-200'
                                    } cursor-pointer hover:scale-105`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-all duration-300 ${footerForm.is_threads_active ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                          <Input
                            id="threads"
                            value={footerForm.socialMedia.threads}
                            onChange={(e) => handleFooterSocialMediaChange('threads', e.target.value)}
                            placeholder="https://threads.net/@yourpage"
                            className={`mt-2 ${isEditing && footerForm.is_threads_active ? inputActiveClass : inputDisabledClass}`}
                            disabled={!isEditing || !footerForm.is_threads_active}
                          />
                        </div>
                      </div>

                    </TabsContent>

                    {/* Contact Information Tab */}
                    <TabsContent value="contact-info" className="space-y-6">
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="contactEmail" className="flex items-center gap-2 mb-2">
                            <Mail className="w-4 h-4 text-blue-600" />
                            {t("content.emailAddress")}
                          </Label>
                          <Input
                            id="contactEmail"
                            type="email"
                            value={footerForm.contactInfo.email}
                            onChange={(e) => handleFooterContactInfoChange('email', e.target.value)}
                            placeholder="Enter email address..."
                            className={isEditing ? inputActiveClass : inputDisabledClass}
                            disabled={footerData && !isEditing}
                          />
                        </div>

                        <div>
                          <Label htmlFor="contactPhone" className="flex items-center gap-2 mb-2">
                            <Phone className="w-4 h-4 text-green-600" />
                            {t("content.phoneNumber")}
                          </Label>
                          <Input
                            id="contactPhone"
                            value={footerForm.contactInfo.phone}
                            onChange={(e) => handleFooterContactInfoChange('phone', e.target.value)}
                            placeholder="Enter phone number..."
                            className={isEditing ? inputActiveClass : inputDisabledClass}
                            disabled={footerData && !isEditing}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: "10px" }}>

                          <div style={{ width: "49%" }}>
                            <Label htmlFor="contactAddress" className="flex items-center gap-2 mb-2">
                              <MapPin className="w-4 h-4 text-red-600" />
                              {t("content.officeAddress")} (English)
                            </Label>
                            <textarea
                              id="contactAddress"
                              value={footerForm.contactInfo.address.en}
                              onChange={(e) => handleFooterContactInfoChange('address.en', e.target.value)}
                              placeholder="Enter office address..."
                              rows={3}
                              className={`w-full px-3 py-2 border rounded-md resize-none text-sm ${isEditing ? inputActiveClass : inputDisabledClass
                                }`}
                              disabled={footerData && !isEditing}
                            />
                          </div>

                          <div style={{ width: "49%" }}>
                            <Label htmlFor="contactAddress" className="flex items-center gap-2 mb-2">
                              <MapPin className="w-4 h-4 text-red-600" />
                              {t("content.officeAddress")} (Tamil)
                            </Label>
                            <textarea
                              id="contactAddress"
                              value={footerForm.contactInfo.address.ta}
                              onChange={(e) => handleFooterContactInfoChange('address.ta', e.target.value)}
                              placeholder="Enter office address..."
                              rows={3}
                              className={`w-full px-3 py-2 border rounded-md resize-none text-sm ${isEditing ? inputActiveClass : inputDisabledClass
                                }`}
                              disabled={footerData && !isEditing}
                            />
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Brand Logo Tab */}
                    <TabsContent value="brand-logo" className="space-y-6">
                      <div className="space-y-4">
                        {footerForm.brandInfo.logo && (
                          <div className="flex flex-col items-start space-y-2">
                            <img
                              src={footerForm.brandInfo.logo}
                              alt="Brand Logo Preview"
                              className="w-16 h-16 object-contain rounded border border-gray-300 bg-white shadow-sm"
                              onError={(e) => (e.target.style.display = "none")}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleImagePreview(footerForm.brandInfo.logo, 'Brand Logo')}
                              className="flex items-center gap-1 text-xs cursor-pointer"
                            >
                              <Eye className="w-3 h-3 cursor-pointer" />
                              {t("content.preview")}
                            </Button>
                          </div>
                        )}
                        <div className="space-y-3">
                          {/* <div>
                          <Label htmlFor="brandLogoUrl" className="flex items-center gap-2 mb-2">
                            Logo URL <span style={{ color: 'red' }}>*</span>
                          </Label>
                          <Input
                            id="brandLogoUrl"
                            value={footerForm.brandInfo.logo}
                            onChange={(e) => handleFooterBrandInfoChange('logo', e.target.value)}
                            placeholder="Enter brand logo URL..."
                            className={`w-full ${formErrors.brandLogo ? 'border-red-500' : isEditing ? inputActiveClass : inputDisabledClass}`}
                            disabled={footerData && !isEditing}
                          />
                          {formErrors.brandLogo && (
                            <p className="text-red-500 text-sm mt-1">{formErrors.brandLogo}</p>
                          )}
                        </div> */}
                          <div className="flex justify-start">
                            <input
                              type="file"
                              id="footer-logo-upload"
                              accept="image/*"
                              onChange={(e) => handleImageUpload(e.target.files[0], 'logo', 'footer')}
                              className="hidden"
                              disabled={(footerData && !isEditing) || uploadingImage}
                            />
                            <Label
                              htmlFor="footer-logo-upload"
                              className={`inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg text-sm cursor-pointer shadow-sm transition-colors ${isEditing && !uploadingImage
                                ? "border-blue-400 bg-blue-50 text-blue-600 hover:bg-blue-100"
                                : "border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                                }`}
                            >
                              <Upload className="w-4 h-4" />
                              {uploadingImage ? t("content.uploading") : (footerForm.brandInfo.logo ? t("content.changeImage") : t("content.uploadImage"))}
                            </Label>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                )}

                {/* Footer Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t">
                  {!footerData ? (
                    <Button
                      onClick={createFooter}
                      disabled={loading}
                      className="flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {loading ? 'Creating...' : 'Create Footer'}
                    </Button>
                  ) : isEditing && (
                    <Button
                      onClick={updateFooter}
                      disabled={loading}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Save className="w-4 h-4 cursor-pointer" />
                      {loading ? 'Updating...' : 'Update Footer'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Terms and Conditions Tab */}
          <TabsContent value="terms" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">Terms & Conditions Configuration</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant={isEditing ? "outline" : "default"}
                    onClick={() => setIsEditing(!isEditing)}
                    className="px-6 py-2 text-white rounded-lg transition-all duration-300 flex items-center gap-2 cursor-pointer"
                    style={{ background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))" }}
                  >
                    <Edit className="w-4 h-4 cursor-pointer" />
                    {isEditing ? t("content.cancelEdit") : "Edit Terms"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="termsTitle" className="mb-2 block">Title</Label>
                  <Input
                    id="termsTitle"
                    value={termsForm.title}
                    onChange={(e) => setTermsForm({ ...termsForm, title: e.target.value })}
                    disabled={!isEditing}
                    className={isEditing ? inputActiveClass : inputDisabledClass}
                  />
                </div>
                <div>
                  <Label className="mb-2 block font-medium text-gray-700">Content</Label>
                  {isEditing ? (
                    <textarea
                      value={termsForm.content}
                      onChange={(e) => setTermsForm({ ...termsForm, content: e.target.value })}
                      rows={15}
                      placeholder="Enter Terms & Conditions content here..."
                      className="w-full px-3 py-2 border border-blue-200 ring-1 ring-blue-100 bg-white rounded-md resize-y text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
                    />
                  ) : (
                    <div
                      className="p-4 bg-gray-50 border rounded min-h-[200px] text-sm text-gray-700 whitespace-pre-wrap"
                    >
                      {termsForm.content || <span className="text-gray-400 italic">No content yet.</span>}
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className="flex justify-end gap-3 pt-6 border-t">
                    <Button onClick={updateTerms} disabled={loading} className="flex items-center gap-2 cursor-pointer">
                      <Save className="w-4 h-4 cursor-pointer" />
                      {loading ? 'Updating...' : 'Update Terms'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Privacy Policy Tab */}
          <TabsContent value="privacy" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">Privacy Policy Configuration</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant={isEditing ? "outline" : "default"}
                    onClick={() => setIsEditing(!isEditing)}
                    className="px-6 py-2 text-white rounded-lg transition-all duration-300 flex items-center gap-2 cursor-pointer"
                    style={{ background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))" }}
                  >
                    <Edit className="w-4 h-4 cursor-pointer" />
                    {isEditing ? t("content.cancelEdit") : "Edit Privacy Policy"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="privacyTitle" className="mb-2 block">Title</Label>
                  <Input
                    id="privacyTitle"
                    value={privacyForm.title}
                    onChange={(e) => setPrivacyForm({ ...privacyForm, title: e.target.value })}
                    disabled={!isEditing}
                    className={isEditing ? inputActiveClass : inputDisabledClass}
                  />
                </div>
                <div>
                  <Label className="mb-2 block font-medium text-gray-700">Content</Label>
                  {isEditing ? (
                    <textarea
                      value={privacyForm.content}
                      onChange={(e) => setPrivacyForm({ ...privacyForm, content: e.target.value })}
                      rows={15}
                      placeholder="Enter Privacy Policy content here..."
                      className="w-full px-3 py-2 border border-blue-200 ring-1 ring-blue-100 bg-white rounded-md resize-y text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
                    />
                  ) : (
                    <div
                      className="p-4 bg-gray-50 border rounded min-h-[200px] text-sm text-gray-700 whitespace-pre-wrap"
                    >
                      {privacyForm.content || <span className="text-gray-400 italic">No content yet.</span>}
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className="flex justify-end gap-3 pt-6 border-t">
                    <Button onClick={updatePrivacy} disabled={loading} className="flex items-center gap-2 cursor-pointer">
                      <Save className="w-4 h-4 cursor-pointer" />
                      {loading ? 'Updating...' : 'Update Privacy Policy'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cancellation Policy Tab */}
          <TabsContent value="cancellation" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">Cancellation & Refund Configuration</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant={isEditing ? "outline" : "default"}
                    onClick={() => setIsEditing(!isEditing)}
                    className="px-6 py-2 text-white rounded-lg transition-all duration-300 flex items-center gap-2 cursor-pointer"
                    style={{ background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))" }}
                  >
                    <Edit className="w-4 h-4 cursor-pointer" />
                    {isEditing ? t("content.cancelEdit") : "Edit Policy"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="cancellationTitle" className="mb-2 block font-medium text-gray-700">Title</Label>
                  <Input
                    id="cancellationTitle"
                    value={cancellationForm.title}
                    onChange={(e) => setCancellationForm({ ...cancellationForm, title: e.target.value })}
                    disabled={!isEditing}
                    className={isEditing ? inputActiveClass : inputDisabledClass}
                  />
                </div>
                <div>
                  <Label className="mb-2 block font-medium text-gray-700">Content</Label>
                  {isEditing ? (
                    <textarea
                      value={cancellationForm.content}
                      onChange={(e) => setCancellationForm({ ...cancellationForm, content: e.target.value })}
                      rows={15}
                      placeholder="Enter Cancellation & Refund Policy content here..."
                      className="w-full px-3 py-2 border border-blue-200 ring-1 ring-blue-100 bg-white rounded-md resize-y text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
                    />
                  ) : (
                    <div
                      className="p-4 bg-gray-50 border rounded min-h-[200px] text-sm text-gray-700 whitespace-pre-wrap"
                    >
                      {cancellationForm.content || <span className="text-gray-400 italic">No content yet.</span>}
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className="flex justify-end gap-3 pt-6 border-t">
                    <Button onClick={updateCancellation} disabled={loading} className="flex items-center gap-2 cursor-pointer">
                      <Save className="w-4 h-4 cursor-pointer" />
                      {loading ? 'Updating...' : 'Update Policy'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shipping Policy Tab */}
          <TabsContent value="shipping" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">Shipping & Exchange Configuration</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant={isEditing ? "outline" : "default"}
                    onClick={() => setIsEditing(!isEditing)}
                    className="px-6 py-2 text-white rounded-lg transition-all duration-300 flex items-center gap-2 cursor-pointer"
                    style={{ background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))" }}
                  >
                    <Edit className="w-4 h-4 cursor-pointer" />
                    {isEditing ? t("content.cancelEdit") : "Edit Policy"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="shippingTitle" className="mb-2 block font-medium text-gray-700">Title</Label>
                  <Input
                    id="shippingTitle"
                    value={shippingForm.title}
                    onChange={(e) => setShippingForm({ ...shippingForm, title: e.target.value })}
                    disabled={!isEditing}
                    className={isEditing ? inputActiveClass : inputDisabledClass}
                  />
                </div>
                <div>
                  <Label className="mb-2 block font-medium text-gray-700">Content</Label>
                  {isEditing ? (
                    <textarea
                      value={shippingForm.content}
                      onChange={(e) => setShippingForm({ ...shippingForm, content: e.target.value })}
                      rows={15}
                      placeholder="Enter Shipping & Exchange Policy content here..."
                      className="w-full px-3 py-2 border border-blue-200 ring-1 ring-blue-100 bg-white rounded-md resize-y text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
                    />
                  ) : (
                    <div
                      className="p-4 bg-gray-50 border rounded min-h-[200px] text-sm text-gray-700 whitespace-pre-wrap"
                    >
                      {shippingForm.content || <span className="text-gray-400 italic">No content yet.</span>}
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className="flex justify-end gap-3 pt-6 border-t">
                    <Button onClick={updateShipping} disabled={loading} className="flex items-center gap-2 cursor-pointer">
                      <Save className="w-4 h-4 cursor-pointer" />
                      {loading ? 'Updating...' : 'Update Policy'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Image Preview Modal with Scroll Control */}
      {
        imagePreview.show && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <ImageIcon className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">{imagePreview.title}</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeImagePreview}
                  className="hover:bg-gray-50 rounded-lg p-2 transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5 cursor-pointer" />
                </Button>
              </div>

              {/* Image Content - No Scroll */}
              <div className="flex-1 p-6 flex items-center justify-center overflow-hidden">
                <img
                  src={imagePreview.url}
                  alt={imagePreview.title}
                  className="max-w-full max-h-full object-contain rounded-lg"
                  style={{ maxHeight: 'calc(90vh - 200px)' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Eye className="w-4 h-4" />
                    <span>Image Preview</span>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={closeImagePreview}
                      className="cursor-pointer"
                    >
                      Close Preview
                    </Button>
                    <Button
                      onClick={() => window.open(imagePreview.url, '_blank')}
                      className="flex items-center gap-2 cursor-pointer bg-blue-600 hover:bg-blue-700"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open Original
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default ContentManagementSection;